import { useMemo, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { HistoryGroup } from '../../lib/catalog'
import type { ForgetResult } from '../../hooks/useItemHistory'
import { fold } from '../../lib/normalize'
import { SheetPanel } from '../SheetPanel'

interface Props {
  open: boolean
  groups: HistoryGroup[]
  loaded: boolean
  /** useForgetItems: exact names, or null for everything. Never Plus: privacy is the household's on any plan. */
  onForget: (names: string[] | null) => Promise<ForgetResult>
  onClose: () => void
}

// Settings › Item history: everything the household has put on the list
// before, as From before and Running low remember it. Forgetting a name
// hard-deletes its past rows (forget_items) — a typo or a one-off stops coming
// back. What is on the list now stays; delete it there first.
export function ItemHistorySheet(props: Props) {
  return <AnimatePresence>{props.open && <Body {...props} />}</AnimatePresence>
}

function Body(p: Props) {
  const [query, setQuery] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const shown = useMemo(() => {
    const q = fold(query)
    return q ? p.groups.filter(g => g.names.some(n => fold(n).includes(q))) : p.groups
  }, [p.groups, query])

  async function forget(key: string, names: string[] | null, label: string) {
    setBusy(key)
    setError(null)
    const res = await p.onForget(names)
    setBusy(null)
    setConfirming(null)
    if (!res.ok) {
      setError('Couldn’t forget that — check your connection and try again')
      return
    }
    setNote(`Forgot ${label}`)
  }

  return (
    <SheetPanel onClose={p.onClose} maxHeight="88dvh" layout="scroll" ariaLabel="Item history" zIndex={12}>
      <div className="ml-history">
        <header className="ml-settings__head">
          <h2 className="ml-display ml-settings__heading">Item history</h2>
          <button type="button" className="ml-btn ml-btn--secondary" onClick={p.onClose}>Done</button>
        </header>
        <p className="ml-settings__sub">
          Names you have bought before, for From before and Running low. Forgetting one deletes it for good,
          for everyone in the household. What is on the list now stays.
        </p>

        <input className="ml-input" type="search" aria-label="Search history" placeholder="Search"
          value={query} onChange={e => setQuery(e.target.value)} />

        {note && <p className="ml-settings__note" role="status">{note}</p>}
        {error && <p className="ml-settings__error" role="alert">{error}</p>}

        {!p.loaded ? (
          <p className="ml-settings__sub">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="ml-settings__sub">{p.groups.length ? 'Nothing matches' : 'Nothing remembered yet'}</p>
        ) : (
          <ul className="ml-history__list" aria-label="Remembered items">
            {shown.map(g => (
              <li key={g.key} className="ml-history__row">
                <span className="ml-history__name">
                  {g.name}
                  {g.names.length > 1 && <span className="ml-history__also">Also {g.names.slice(1).join(', ')}</span>}
                </span>
                {confirming === g.key ? (
                  <span className="ml-history__confirm">
                    <button type="button" className="ml-text-btn" onClick={() => setConfirming(null)}>Keep</button>
                    <button type="button" className="ml-text-btn ml-text-btn--destructive" disabled={busy === g.key}
                      onClick={() => forget(g.key, g.names, `“${g.name}”`)}>
                      Forget
                    </button>
                  </span>
                ) : (
                  <button type="button" className="ml-text-btn ml-text-btn--destructive" aria-label={`Forget ${g.name}`}
                    onClick={() => setConfirming(g.key)}>
                    Forget
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {p.loaded && p.groups.length > 0 && (
          <div className="ml-settings__card">
            {confirming === '*' ? (
              <>
                <p className="ml-settings__line ml-settings__line--warn">Forget all {p.groups.length} names?</p>
                <p className="ml-settings__sub">From before and Running low start over. This can’t be undone.</p>
                <div className="ml-settings__actions">
                  <button type="button" className="ml-text-btn" onClick={() => setConfirming(null)}>Cancel</button>
                  <button type="button" className="ml-text-btn ml-text-btn--destructive" disabled={busy === '*'}
                    onClick={() => forget('*', null, 'the whole history')}>
                    Forget everything
                  </button>
                </div>
              </>
            ) : (
              <button type="button" className="ml-text-btn ml-text-btn--destructive"
                onClick={() => setConfirming('*')}>
                Forget the whole history
              </button>
            )}
          </div>
        )}
      </div>
    </SheetPanel>
  )
}
